'use client'

import { useState, useEffect, useCallback } from 'react'
import { Bell } from 'lucide-react'

import { useSafeDate } from '@/hooks/useSafeDate'
import ClientOnly from '@/components/ClientOnly'
import { notificationApi, Notification } from '@/lib/api'
import { useUser } from '@/hooks/useUser'

interface NotificationSystemProps {
  userId: string
}

export default function NotificationSystem({ userId: _userId }: NotificationSystemProps) {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)
  const [showNotifications, setShowNotifications] = useState(false)
  const { formatRelativeTime } = useSafeDate()
  const { token } = useUser()

  // Poll interval; back off on 429 to avoid hammering the API
  const [pollIntervalMs, setPollIntervalMs] = useState(45000) // 45s default (reduces rate-limit pressure)

  // Load notifications
  const loadNotifications = useCallback(async () => {
    if (!token) {
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const response = await notificationApi.getNotifications(token, {
        limit: 20,
        page: 1
      })

      // Reset poll interval on success (may have been increased after 429)
      if (response.success && pollIntervalMs > 45000) {
        setPollIntervalMs(45000)
      }

      if (response.success && response.data) {
        // Normalize notification data (handle both isRead and is_read)
        const normalizedNotifications = response.data.notifications.map(n => ({
          ...n,
          id: n._id || n.id,
          is_read: n.isRead !== undefined ? n.isRead : n.is_read,
          created_at: n.createdAt || n.created_at
        }))

        setNotifications(normalizedNotifications)
        setUnreadCount(response.data.unreadCount || 0)
      } else {
        setNotifications([])
        setUnreadCount(0)
        // On 429 (rate limit), back off: double interval up to 3 minutes
        const isRateLimited = response.error && (String(response.error).includes('429') || String(response.error).toLowerCase().includes('too many'))
        if (isRateLimited) {
          setPollIntervalMs(prev => Math.min(prev * 2, 180000))
        }
      }
    } catch (error: unknown) {
      console.error('Error loading notifications:', error)
      setNotifications([])
      setUnreadCount(0)
    } finally {
      setLoading(false)
    }
  }, [token, pollIntervalMs])

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    if (!token) return

    try {
      // Optimistically update UI
      setNotifications(prev =>
        prev.map(n => {
          const id = n._id || n.id
          return id === notificationId ? { ...n, is_read: true, isRead: true } : n
        })
      )
      setUnreadCount(prev => Math.max(0, prev - 1))

      // Call API
      await notificationApi.markAsRead(notificationId, token)
    } catch (error) {
      console.error('Error marking notification as read:', error)
      // Revert on error
      loadNotifications()
    }
  }

  // Mark all as read
  const markAllAsRead = async () => {
    if (!token) return

    try {
      // Optimistically update UI
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, isRead: true })))
      setUnreadCount(0)

      // Call API
      await notificationApi.markAllAsRead(token)
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      // Revert on error
      loadNotifications()
    }
  }

  // Get notification icon based on type
  const getNotificationIcon = (type: string) => {
    // Map backend notification types to icons
    if (type.includes('application')) {
      return (
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    }
    if (type.includes('payment')) {
      return (
        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      )
    }
    if (type.includes('message')) {
      return (
        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      )
    }
    if (type.includes('maintenance') || type.includes('viewing')) {
      return (
        <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
    // Default icon
    return (
      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }

  // Use safe date formatting to prevent hydration mismatches
  // formatRelativeTime is imported from useSafeDate hook

  useEffect(() => {
    loadNotifications()

    const interval = setInterval(() => {
      loadNotifications()
    }, pollIntervalMs)

    return () => clearInterval(interval)
  }, [loadNotifications, pollIntervalMs])

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={() => setShowNotifications(!showNotifications)}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-6 h-6" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold shadow-sm">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Mark all as read
                </button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-600">Loading notifications...</p>
              </div>
            ) : notifications.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {notifications.map((notification) => {
                  const notifId = notification._id || notification.id
                  const isRead = notification.isRead !== undefined ? notification.isRead : notification.is_read
                  const createdAt = notification.createdAt || notification.created_at
                  
                  return (
                    <div
                      key={notifId}
                      className={`p-4 hover:bg-gray-50 cursor-pointer ${
                        !isRead ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => markAsRead(notifId)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start">
                            <p className="text-sm font-medium text-gray-900">
                              {notification.title}
                            </p>
                            <p className="text-xs text-gray-500 ml-2">
                              <ClientOnly fallback="...">
                                {formatRelativeTime(createdAt)}
                              </ClientOnly>
                            </p>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {notification.message}
                          </p>
                          {!isRead && (
                            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
                <div className="p-4 text-center text-gray-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No notifications yet</p>
                  <p className="text-xs text-gray-400">You&apos;ll see updates here</p>
                </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowNotifications(false)}
                className="w-full text-sm text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {showNotifications && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowNotifications(false)}
        />
      )}
    </div>
  )
} 

